"use client";

import { useEffect, useState } from "react";
import NavBar from "../../home-page/components/NavBar";
import FooterSection from "../../home-page/components/FooterSection";
import Link from "next/link";
import { generateBreadcrumbSchema, generateOrganizationSchema } from '@/lib/utils/metadata';
import { testimonialService, Testimonial } from '@/lib/services/testimonialService';
import { landingPageService, LandingPageContent } from '@/lib/services/landingPageService';
import { retailerApplicationService } from '@/lib/services/retailerApplicationService';
import AppIcon from '@/components/ui/AppIcon';

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || '';

const convertIconName = (iconName: string): string => {
  if (!iconName || typeof iconName !== 'string') return 'QuestionMarkCircleIcon';
  return iconName
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('') + 'Icon';
};

interface FormData {
  businessName: string;
  contactPerson: string;
  email: string;
  phone: string;
  businessType: string;
  businessAddress: string;
  currentDeliveryVolume: string;
  message: string;
}

const BUSINESS_TYPES = [
  'Grocery & Food',
  'Pharmacy & Health',
  'Electronics & Tech',
  'Clothing & Apparel',
  'Home & Furniture',
  'Books & Stationery',
  'Flowers & Gifts',
  'Pet Supplies',
  'Hardware & Tools',
  'Other Retail',
];

const DELIVERY_VOLUMES = [
  'Less than 50 deliveries/month',
  '50 - 200 deliveries/month',
  '200 - 500 deliveries/month',
  '500 - 1,000 deliveries/month',
  'More than 1,000 deliveries/month',
];

export default function RetailersLandingPage() {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [contentLoading, setContentLoading] = useState(true);
  const [heroStats, setHeroStats] = useState<any>(null);
  const [benefits, setBenefits] = useState<any[]>([]);
  const [whyChoose, setWhyChoose] = useState<any[]>([]);
  const [howItWorks, setHowItWorks] = useState<any[]>([]);
  const [contentError, setContentError] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState<FormData>({
    businessName: '',
    contactPerson: '',
    email: '',
    phone: '',
    businessType: '',
    businessAddress: '',
    currentDeliveryVolume: '',
    message: '',
  });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    const loadTestimonials = async () => {
      try {
        const data = await testimonialService.getActive();
        setTestimonials(data.slice(0, 3));
      } catch (error) {
        console.error('Failed to load testimonials:', error);
      } finally {
        setLoading(false);
      }
    };
    loadTestimonials();
  }, []);

  useEffect(() => {
    const loadContent = async () => {
      try {
        setContentLoading(true);
        setContentError(null);
        const content = await landingPageService.getByPageKey('retailers' as any);

        content.forEach((item: LandingPageContent) => {
          if (!item.isActive) return;
          switch (item.sectionKey) {
            case 'hero':
              setHeroStats(item.content);
              break;
            case 'benefits':
              setBenefits(item.content?.items || []);
              break;
            case 'whyChoose':
              setWhyChoose(item.content?.items || []);
              break;
            case 'howItWorks':
              setHowItWorks(item.content?.items || []);
              break;
          }
        });
      } catch (error) {
        console.error('Failed to load landing page content:', error);
        setContentError('Failed to load content');
        setFallbackContent();
      } finally {
        setContentLoading(false);
      }
    };
    loadContent();
  }, []);

  const setFallbackContent = () => {
    setHeroStats({
      retailers: '200+',
      deliveries: '50K+',
      cities: '5 Cities',
      satisfaction: '98%'
    });

    setBenefits([
      { icon: 'chart-bar-square', title: 'Increase Sales', description: 'Expand your customer base by offering fast, reliable delivery. Reach customers who prefer shopping online or need same-day delivery.' },
      { icon: 'globe-alt', title: 'Expand Your Reach', description: 'Deliver beyond your local area. GoGedit connects your store to customers across Ontario with our growing driver network.' },
      { icon: 'truck', title: 'Reliable Delivery', description: 'Professional, vetted drivers handle your products with care. Real-time tracking keeps you and your customers informed.' },
      { icon: 'adjustments-horizontal', title: 'Flexible Solutions', description: 'From same-day to scheduled deliveries, choose the service model that fits your business needs and customer expectations.' },
      { icon: 'banknotes', title: 'Cost-Effective', description: 'No need to maintain your own fleet. Pay only for deliveries made, reducing overhead and maximizing your profit margins.' },
      { icon: 'star', title: 'Brand Partnership', description: 'Co-brand your delivery experience. Your customers see your brand, not ours, building loyalty and trust with every delivery.' }
    ]);

    setWhyChoose([
      { icon: 'bolt', title: 'Fast Onboarding', description: 'Get up and running in days, not months. Our simple integration process means you can start delivering quickly.' },
      { icon: 'device-phone-mobile', title: 'Easy Integration', description: 'Seamlessly connect GoGedit with your existing POS, e-commerce platform, or inventory management system.' },
      { icon: 'chart-pie', title: 'Analytics Dashboard', description: 'Access real-time delivery analytics, customer insights, and performance metrics to optimize your operations.' },
      { icon: 'shield-check', title: 'Insured Deliveries', description: 'Every delivery is fully insured. Your products are protected from pickup to drop-off, giving you peace of mind.' }
    ]);

    setHowItWorks([
      { step: '1', title: 'Sign Up', description: 'Create your retailer account in minutes. Tell us about your business, delivery volume, and service area.' },
      { step: '2', title: 'Integrate', description: 'Connect GoGedit to your existing systems. Our team provides full technical support throughout the setup.' },
      { step: '3', title: 'Start Delivering', description: 'Go live and start fulfilling orders. Our driver network is ready to pick up and deliver your products.' },
      { step: '4', title: 'Track Performance', description: 'Monitor delivery metrics, customer satisfaction, and business growth through your dedicated retailer dashboard.' }
    ]);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormError(null);

    try {
      const result = await retailerApplicationService.submitApplication(formData);
      if (result) {
        setFormSuccess(true);
        setFormData({
          businessName: '',
          contactPerson: '',
          email: '',
          phone: '',
          businessType: '',
          businessAddress: '',
          currentDeliveryVolume: '',
          message: '',
        });
      } else {
        setFormError('Failed to submit application. Please try again.');
      }
    } catch (error: any) {
      setFormError(error.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setFormSubmitting(false);
    }
  };

  return (
    <>
      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Retailers - Partner with GoGedit for Delivery',
            description: 'Grow your retail business with GoGedit. Reliable delivery partner helping retailers increase sales, expand reach, and delight customers across Ontario.',
            url: `${SITE_URL}/landing/retailers`,
            breadcrumb: generateBreadcrumbSchema([
              { name: 'Home', url: `${SITE_URL}/home-page` },
              { name: 'Landing', url: `${SITE_URL}/landing` },
              { name: 'Retailers', url: `${SITE_URL}/landing/retailers` }
            ])
          })
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateOrganizationSchema())
        }}
      />

      <NavBar />

      {/* Hero Section */}
      <section className="hero-gradient relative overflow-hidden py-20 md:py-32">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-brand-purple/5 -translate-y-1/4 translate-x-1/4 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-brand-green/5 translate-y-1/4 -translate-x-1/4 pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-brand-green/20 shadow-green-md">
              <span className="w-2 h-2 rounded-full bg-brand-green animate-pulse" />
              <span className="text-sm font-semibold text-dark-navy">For Retailers</span>
            </div>

            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight text-deep-navy">
              Grow Your Business. <span className="text-brand-green">Deliver More.</span>
            </h1>

            <p className="text-lg md:text-xl text-body-text leading-relaxed">
              {contentLoading ? (
                <span className="inline-block w-64 h-6 bg-gray-200 animate-pulse rounded" />
              ) : (
                `Partner with GoGedit to reach ${heroStats?.retailers || '200+'} retailers already growing their business. Reliable delivery solutions that drive sales and delight customers across ${heroStats?.cities || '5 Cities'}.`
              )}
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4 pt-4">
              <a
                href="#partner-signup"
                className="btn-green"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('partner-signup')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                <AppIcon name="BuildingStorefrontIcon" size={18} className="text-white" />
                Partner With Us
              </a>
              <a
                href="#how-it-works"
                className="btn-secondary"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Learn More
              </a>
            </div>

            {contentLoading ? (
              <div className="flex items-center justify-center gap-8 pt-6">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex items-center gap-8">
                    <div className="w-20 h-16 bg-gray-200 animate-pulse rounded" />
                    {i < 4 && <div className="w-px h-10 bg-border-gray" />}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap items-center justify-center gap-6 pt-6">
                <div className="text-center">
                  <p className="font-heading font-800 text-2xl text-brand-green">{heroStats?.retailers || '200+'}</p>
                  <p className="text-xs text-muted-text font-medium uppercase tracking-wider mt-1">Retail Partners</p>
                </div>
                <div className="w-px h-10 bg-border-gray hidden sm:block" />
                <div className="text-center">
                  <p className="font-heading font-800 text-2xl text-brand-purple">{heroStats?.deliveries || '50K+'}</p>
                  <p className="text-xs text-muted-text font-medium uppercase tracking-wider mt-1">Deliveries</p>
                </div>
                <div className="w-px h-10 bg-border-gray hidden sm:block" />
                <div className="text-center">
                  <p className="font-heading font-800 text-2xl text-brand-green">{heroStats?.cities || '5 Cities'}</p>
                  <p className="text-xs text-muted-text font-medium uppercase tracking-wider mt-1">Coverage</p>
                </div>
                <div className="w-px h-10 bg-border-gray hidden sm:block" />
                <div className="text-center">
                  <p className="font-heading font-800 text-2xl text-brand-purple">{heroStats?.satisfaction || '98%'}</p>
                  <p className="text-xs text-muted-text font-medium uppercase tracking-wider mt-1">Satisfaction</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-deep-navy mb-4">
              Drive Growth with <span className="text-brand-green">GoGedit</span>
            </h2>
            <p className="text-lg text-body-text">
              Everything your retail business needs to take delivery to the next level.
            </p>
          </div>

          {contentLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-gray-200 animate-pulse rounded-2xl h-48" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="group bg-white rounded-2xl p-6 shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1 border border-border-gray/50"
                >
                  <div className="w-14 h-14 rounded-xl bg-brand-green/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    {typeof benefit.icon === 'string' ? (
                      <AppIcon
                        name={convertIconName(benefit.icon)}
                        size={28}
                        className="text-brand-green"
                      />
                    ) : (
                      benefit.icon
                    )}
                  </div>
                  <h3 className="font-heading text-xl font-bold text-deep-navy mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-body-text leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Why Choose GoGedit Section */}
      <section className="py-20 bg-light-gray">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-deep-navy mb-4">
              Why Choose <span className="text-brand-purple">GoGedit</span>?
            </h2>
            <p className="text-lg text-body-text">
              We make it easy to get started and grow with us.
            </p>
          </div>

          {contentLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-gray-200 animate-pulse rounded-2xl h-40" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {whyChoose.map((item, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-6 shadow-card text-center hover:shadow-card-hover transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-full bg-brand-purple/10 flex items-center justify-center mx-auto mb-4">
                    {typeof item.icon === 'string' ? (
                      <AppIcon
                        name={convertIconName(item.icon)}
                        size={24}
                        className="text-brand-purple"
                      />
                    ) : (
                      item.icon
                    )}
                  </div>
                  <h3 className="font-heading text-lg font-bold text-deep-navy mb-2">{item.title}</h3>
                  <p className="text-sm text-body-text leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-deep-navy mb-4">
              How It <span className="text-brand-green">Works</span>
            </h2>
            <p className="text-lg text-body-text">
              Getting started with GoGedit is simple and fast.
            </p>
          </div>

          {contentLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-gray-200 animate-pulse rounded-2xl h-48" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {howItWorks.map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 rounded-full bg-brand-green flex items-center justify-center mx-auto mb-4 text-white font-heading font-bold text-2xl shadow-green-md">
                    {step.step || index + 1}
                  </div>
                  <h3 className="font-heading text-xl font-bold text-deep-navy mb-2">{step.title}</h3>
                  <p className="text-body-text leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Partner Signup Form Section */}
      <section id="partner-signup" className="py-20 bg-light-gray">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-green/10 border border-brand-green/20 mb-4">
              <AppIcon name="BuildingStorefrontIcon" size={16} className="text-brand-green" />
              <span className="text-sm font-semibold text-brand-green">Retailer Partnership</span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-deep-navy mb-4">
              Partner With <span className="text-brand-green">Us</span>
            </h2>
            <p className="text-lg text-body-text">
              Fill out the form below and our team will reach out within 1-2 business days to discuss how GoGedit can help grow your business.
            </p>
          </div>

          {formSuccess ? (
            <div className="bg-white rounded-2xl shadow-card p-10 text-center">
              <div className="w-20 h-20 rounded-full bg-brand-green/10 flex items-center justify-center mx-auto mb-6">
                <AppIcon name="CheckCircleIcon" size={40} className="text-brand-green" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-deep-navy mb-3">Application Submitted!</h3>
              <p className="text-body-text mb-6">
                Thank you for your interest in partnering with GoGedit. Our team will review your application and contact you within 1-2 business days.
              </p>
              <button
                onClick={() => setFormSuccess(false)}
                className="btn-green"
              >
                Submit Another Application
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-card p-8">
              {formError && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                  <AppIcon name="ExclamationCircleIcon" size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{formError}</p>
                </div>
              )}

              <form onSubmit={handleFormSubmit} className="space-y-6">
                {/* Business Information */}
                <div>
                  <h3 className="font-heading text-lg font-bold text-deep-navy mb-4 flex items-center gap-2">
                    <AppIcon name="BuildingOffice2Icon" size={20} className="text-brand-green" />
                    Business Information
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-navy-dark mb-1.5">
                        Business Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="businessName"
                        value={formData.businessName}
                        onChange={handleInputChange}
                        required
                        placeholder="Your Business Name"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-navy-dark mb-1.5">
                        Business Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="businessType"
                        value={formData.businessType}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent text-sm"
                      >
                        <option value="">Select Business Type</option>
                        {BUSINESS_TYPES.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-navy-dark mb-1.5">
                      Business Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="businessAddress"
                      value={formData.businessAddress}
                      onChange={handleInputChange}
                      required
                      placeholder="Full business address"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent text-sm"
                    />
                  </div>
                </div>

                <hr className="border-border-gray" />

                {/* Contact Information */}
                <div>
                  <h3 className="font-heading text-lg font-bold text-deep-navy mb-4 flex items-center gap-2">
                    <AppIcon name="UserIcon" size={20} className="text-brand-green" />
                    Contact Information
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-navy-dark mb-1.5">
                        Contact Person <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="contactPerson"
                        value={formData.contactPerson}
                        onChange={handleInputChange}
                        required
                        placeholder="Full name"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-navy-dark mb-1.5">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="business@example.com"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-navy-dark mb-1.5">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        placeholder="(416) 555-0000"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-navy-dark mb-1.5">
                        Current Delivery Volume <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="currentDeliveryVolume"
                        value={formData.currentDeliveryVolume}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent text-sm"
                      >
                        <option value="">Select Volume</option>
                        {DELIVERY_VOLUMES.map(vol => (
                          <option key={vol} value={vol}>{vol}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <hr className="border-border-gray" />

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-navy-dark mb-1.5">
                    Message / Requirements
                    <span className="text-muted-text font-normal ml-1">(Optional)</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    placeholder="Tell us about your delivery needs, special requirements, or any questions you have..."
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent text-sm resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={formSubmitting}
                  className="w-full btn-green justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {formSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <AppIcon name="PaperAirplaneIcon" size={18} className="text-white" />
                      Submit Partnership Application
                    </>
                  )}
                </button>
              </form>
            </div>
          )}
        </div>
      </section>

      {/* Testimonials Section */}
      {!loading && testimonials.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center max-w-2xl mx-auto mb-16">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-deep-navy mb-4">
                What Our <span className="text-brand-green">Partners Say</span>
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="bg-light-gray rounded-2xl p-6 shadow-card">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating || 5)].map((_, i) => (
                      <AppIcon key={i} name="StarIcon" size={16} className="text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-body-text leading-relaxed mb-4 italic">&ldquo;{testimonial.content}&rdquo;</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-brand-green/20 flex items-center justify-center">
                      <span className="text-brand-green font-bold text-sm">
                        {testimonial.customerName?.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-deep-navy text-sm">{testimonial.customerName}</p>
                      {testimonial.customerRole && (
                        <p className="text-xs text-muted-text">{testimonial.customerRole}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Cross-links Section */}
      <section className="py-16 bg-light-gray">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-deep-navy mb-3">
              Explore More with <span className="text-brand-green">GoGedit</span>
            </h2>
            <p className="text-body-text">Discover how GoGedit serves everyone in the delivery ecosystem.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            <Link
              href="/landing/users"
              className="group bg-white rounded-2xl p-6 shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1 border border-border-gray/50 text-center"
            >
              <div className="w-12 h-12 rounded-xl bg-brand-purple/10 flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                <AppIcon name="UserGroupIcon" size={24} className="text-brand-purple" />
              </div>
              <h3 className="font-heading text-lg font-bold text-deep-navy mb-1">For Users</h3>
              <p className="text-sm text-body-text">Need delivery services? Sign up as a user.</p>
            </Link>
            <Link
              href="/landing/drivers"
              className="group bg-white rounded-2xl p-6 shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1 border border-border-gray/50 text-center"
            >
              <div className="w-12 h-12 rounded-xl bg-brand-green/10 flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                <AppIcon name="TruckIcon" size={24} className="text-brand-green" />
              </div>
              <h3 className="font-heading text-lg font-bold text-deep-navy mb-1">For Drivers</h3>
              <p className="text-sm text-body-text">Earn money on your schedule as a driver.</p>
            </Link>
          </div>
        </div>
      </section>

      <FooterSection />
    </>
  );
}
